import React, { useMemo, useState } from 'react';
import { useFitStore } from '../../store/useFitStore';
import type {
  Session,
  SessionStatus,
  WorkoutSection,
  PlannedExercise,
  SetPrescription,
  MeasureType,
  WeightUnit,
} from '../../store/types';

/* ============================================================
   Session Editor — Phase 3 of the Weekly Programming system.
   Customizable sections + structured set fields (measure type
   per exercise: Weight × Reps / Time / Laps / Bodyweight /
   Other) + last-week reference shown faintly beside each
   exercise.
============================================================ */

// Set parsing/formatting lives in src/lib/sets.ts (shared with the live
// logger, exercise picker, Training Log, and the history importer).
import { parseSet, setToText, inferMeasure, getDefaultUnit, fmtSets, MEASURE_LABELS } from '../../lib/sets';
import ExercisePicker from './ExercisePicker';
import ExerciseMenu, { type MenuPick } from './ExerciseMenu';

/* ---------- editable draft (per-set fields held as raw strings) ---------- */
interface DraftSet {
  w: string;    // weight/load
  r: string;    // reps
  s: string;    // seconds
  l: string;    // laps
  raw: string;  // free text ("Blue band x 15") for measure 'other'
}
interface DraftExercise {
  id: string;
  name: string;
  note: string;
  measure: MeasureType;
  unit: WeightUnit;
  sets: DraftSet[];
  supersetId?: string;   // carried through so editing doesn't break superset grouping
  videoUrl?: string;
}
interface DraftSection {
  id: string;
  type: WorkoutSection['type'];
  title: string;
  exercises: DraftExercise[];
}

let _uid = 0;
const uid = (prefix: string) => `${prefix}_${Date.now()}_${++_uid}`;

const blankSet = (): DraftSet => ({ w: '', r: '', s: '', l: '', raw: '' });

const toDraftSet = (set: SetPrescription): DraftSet => ({
  w: set.load ?? '',
  r: set.reps ?? '',
  s: set.seconds != null ? String(set.seconds) : '',
  l: set.laps != null ? String(set.laps) : '',
  raw: setToText(set),
});

// Only the fields the chosen measure shows make it into the saved set —
// switching the measure mid-edit can't smuggle stale values along.
const fromDraftSet = (d: DraftSet, measure: MeasureType): SetPrescription => {
  switch (measure) {
    case 'weight_reps': {
      const s: SetPrescription = {};
      if (d.w.trim()) s.load = d.w.trim();
      if (d.r.trim()) s.reps = d.r.trim();
      return s;
    }
    case 'time':
      return d.s.trim() && !isNaN(parseFloat(d.s)) ? { seconds: Math.round(parseFloat(d.s)) } : {};
    case 'laps': {
      const s: SetPrescription = {};
      if (d.w.trim()) s.load = d.w.trim();
      if (d.l.trim() && !isNaN(parseInt(d.l, 10))) s.laps = parseInt(d.l, 10);
      return s;
    }
    case 'bodyweight':
      return d.r.trim() ? { reps: d.r.trim() } : {};
    default:
      return parseSet(d.raw);
  }
};

const toDraftExercise = (ex: PlannedExercise): DraftExercise => ({
  id: ex.id,
  name: ex.name,
  note: ex.note || '',
  measure: ex.measure ?? inferMeasure(ex.sets),
  unit: ex.unit ?? getDefaultUnit(),
  sets: ex.sets.length ? ex.sets.map(toDraftSet) : [blankSet()],
  supersetId: ex.supersetId,
  videoUrl: ex.videoUrl,
});

const toDraftSections = (sections: WorkoutSection[]): DraftSection[] =>
  sections.map((sec) => ({
    id: sec.id,
    type: sec.type,
    title: sec.title,
    exercises: sec.exercises.map(toDraftExercise),
  }));

const fromDraftSections = (sections: DraftSection[]): WorkoutSection[] =>
  sections
    .map((sec) => ({
      id: sec.id,
      type: sec.type,
      title: sec.title.trim() || 'Section',
      exercises: sec.exercises
        .filter((ex) => ex.name.trim())
        .map((ex): PlannedExercise => ({
          id: ex.id,
          name: ex.name.trim(),
          note: ex.note.trim() || undefined,
          supersetId: ex.supersetId,
          videoUrl: ex.videoUrl?.trim() || undefined,
          measure: ex.measure,
          unit: ex.measure === 'weight_reps' || ex.measure === 'laps' ? ex.unit : undefined,
          sets: ex.sets.map((d) => fromDraftSet(d, ex.measure)).filter((s, i, all) =>
            // keep empty sets only if the exercise has no filled ones (e.g. mobility drills)
            Object.keys(s).length > 0 || all.every((x) => Object.keys(x).length === 0) && i === 0
          ),
        })),
    }))
    .filter((sec) => sec.exercises.length > 0 || sec.title.trim() !== '');

const blankExercise = (): DraftExercise => ({
  id: uid('pe'), name: '', note: '', measure: 'weight_reps', unit: getDefaultUnit(), sets: [blankSet()], videoUrl: undefined,
});

export const defaultSections = (): WorkoutSection[] => [
  { id: uid('sec'), type: 'main', title: 'Main', exercises: [] },
  { id: uid('sec'), type: 'mobility', title: 'Mobility / Weakness', exercises: [] },
  { id: uid('sec'), type: 'cooldown', title: 'Cooldown / Core', exercises: [] },
];

const STATUSES: SessionStatus[] = ['planned', 'partial', 'completed', 'missed'];

interface Props {
  session: Session;     // existing session, or a fresh draft for "new"
  isNew: boolean;
  clientName: string;
  onClose: () => void;
}

const SessionEditor: React.FC<Props> = ({ session, isNew, clientName, onClose }) => {
  const { sessions, upsertSession, deleteSession, showToast } = useFitStore();

  const [title, setTitle] = useState(session.title);
  const [status, setStatus] = useState<SessionStatus>(session.status);
  const [sections, setSections] = useState<DraftSection[]>(() => {
    const drafts = toDraftSections(session.sections);
    // a brand-new session starts with an empty row in Main so typing can begin immediately
    if (isNew && drafts.length && drafts.every((s) => s.exercises.length === 0)) {
      drafts[0].exercises.push(blankExercise());
    }
    return drafts;
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Which section the exercise-menu drawer is adding into (null = closed).
  const [menuFor, setMenuFor] = useState<string | null>(null);

  // One tap in the menu drops the exercise into the open section with the
  // client's last-performed sets prefilled; fully-blank starter rows give way.
  const addFromMenu = (pick: MenuPick) => {
    const picked = pick.setTexts.map(parseSet);
    const fresh: DraftExercise = {
      ...blankExercise(),
      name: pick.name,
      measure: inferMeasure(picked),
      sets: picked.length ? picked.map(toDraftSet) : [blankSet()],
    };
    const isUntouched = (e: DraftExercise) =>
      !e.name.trim() && !e.note.trim() && e.sets.every((d) => !d.w && !d.r && !d.s && !d.l && !d.raw);
    setSections((all) =>
      all.map((s) =>
        s.id === menuFor
          ? { ...s, exercises: [...s.exercises.filter((e) => !isUntouched(e)), fresh] }
          : s
      )
    );
  };

  /* ---------- last-week reference ----------
     Index every exercise the client did in the 7 days before this
     session's week, keyed by lowercase name. */
  const lastWeekByName = useMemo(() => {
    const d = new Date(session.date + 'T00:00:00');
    const dow = (d.getDay() + 6) % 7; // Monday = 0
    const weekStart = new Date(d);
    weekStart.setDate(d.getDate() - dow);
    const prevStart = new Date(weekStart);
    prevStart.setDate(weekStart.getDate() - 7);
    const iso = (x: Date) =>
      `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
    const lo = iso(prevStart);
    const hi = iso(new Date(weekStart.getTime() - 86400000));

    const map: Record<string, string> = {};
    sessions
      .filter((s) => s.clientId === session.clientId && s.date >= lo && s.date <= hi)
      .forEach((s) =>
        s.sections.forEach((sec) =>
          sec.exercises.forEach((ex) => {
            const label = fmtSets(ex.sets, ex.measure, ex.unit);
            if (label) map[ex.name.trim().toLowerCase()] = label;
          })
        )
      );
    return map;
  }, [sessions, session.clientId, session.date]);

  /* ---------- draft mutations ---------- */
  const patchSection = (sid: string, patch: Partial<DraftSection>) =>
    setSections((all) => all.map((s) => (s.id === sid ? { ...s, ...patch } : s)));
  const patchExercise = (sid: string, eid: string, patch: Partial<DraftExercise>) =>
    setSections((all) =>
      all.map((s) =>
        s.id === sid
          ? { ...s, exercises: s.exercises.map((e) => (e.id === eid ? { ...e, ...patch } : e)) }
          : s
      )
    );
  const patchSet = (sid: string, eid: string, idx: number, patch: Partial<DraftSet>) =>
    setSections((all) =>
      all.map((s) =>
        s.id === sid
          ? {
              ...s,
              exercises: s.exercises.map((e) =>
                e.id === eid
                  ? { ...e, sets: e.sets.map((d, j) => (j === idx ? { ...d, ...patch } : d)) }
                  : e
              ),
            }
          : s
      )
    );
  const moveSection = (sid: string, dir: -1 | 1) =>
    setSections((all) => {
      const i = all.findIndex((s) => s.id === sid);
      const j = i + dir;
      if (j < 0 || j >= all.length) return all;
      const next = [...all];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const save = () => {
    const finalSections = fromDraftSections(sections);
    const exerciseCount = finalSections.reduce((n, s) => n + s.exercises.length, 0);
    if (exerciseCount === 0) {
      showToast('Add at least one exercise', 'error');
      return;
    }
    upsertSession({
      ...session,
      title: title.trim() || 'Workout',
      status,
      sections: finalSections.filter((s) => s.exercises.length > 0),
    });
    showToast(isNew ? 'Session added' : 'Session saved');
    onClose();
  };

  const remove = () => {
    deleteSession(session.id);
    showToast('Session deleted');
    onClose();
  };

  const dateLabel = new Date(session.date + 'T00:00:00').toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        style={{ maxWidth: 680 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card flex flex-col" style={{ maxHeight: '88vh' }}>
          {/* ---------- header ---------- */}
          <div className="flex items-start justify-between gap-3 mb-1">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Session title — e.g. Leg Day"
              className="bg-transparent text-xl font-bold tracking-tight outline-none flex-1 min-w-0 border-b border-transparent focus:border-fit-line-strong pb-0.5"
            />
            <button onClick={onClose} className="text-fit-text-secondary hover:text-white text-lg leading-none shrink-0">×</button>
          </div>
          <p className="mono-label mb-3">{clientName} · {dateLabel}</p>

          {/* status pills */}
          <div className="flex items-center gap-1 mb-4">
            {STATUSES.map((st) => (
              <button
                key={st}
                onClick={() => setStatus(st)}
                className={`px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] transition-colors ${
                  status === st ? 'bg-white text-fit-ink' : 'bg-fit-bg-tertiary text-fit-text-secondary hover:text-white'
                }`}
              >
                {st}
              </button>
            ))}
          </div>

          {/* ---------- sections ---------- */}
          <div className="flex flex-col gap-5 overflow-y-auto no-scrollbar flex-1 pr-1">
            {sections.map((sec, secIdx) => (
              <div key={sec.id} className="rounded-fit-md border border-fit-line p-3">
                <div className="flex items-center gap-2 mb-2">
                  <input
                    value={sec.title}
                    onChange={(e) => patchSection(sec.id, { title: e.target.value })}
                    className="bg-transparent outline-none flex-1 min-w-0 text-[11px] font-semibold uppercase tracking-[0.1em] text-fit-text-secondary focus:text-white border-b border-transparent focus:border-fit-line-strong"
                  />
                  <button onClick={() => moveSection(sec.id, -1)} disabled={secIdx === 0} className="text-fit-muted hover:text-white disabled:opacity-25 text-[12px] px-1" aria-label="Move section up">↑</button>
                  <button onClick={() => moveSection(sec.id, 1)} disabled={secIdx === sections.length - 1} className="text-fit-muted hover:text-white disabled:opacity-25 text-[12px] px-1" aria-label="Move section down">↓</button>
                  <button onClick={() => setSections((all) => all.filter((s) => s.id !== sec.id))} className="text-fit-muted hover:text-white text-[13px] px-1" aria-label="Remove section">×</button>
                </div>

                <div className="flex flex-col gap-3">
                  {sec.exercises.map((ex) => {
                    const lastWk = ex.name.trim() ? lastWeekByName[ex.name.trim().toLowerCase()] : undefined;
                    return (
                      <div key={ex.id} className="border-b border-fit-line pb-3 last:border-0 last:pb-0">
                        <div className="flex items-center gap-2">
                          <ExercisePicker
                            value={ex.name}
                            onChange={(name) => patchExercise(sec.id, ex.id, { name })}
                            onPick={(g) => {
                              // Reach-for prefill: only seed default sets/reps when the
                              // coach hasn't logged any actuals yet — never clobber typed sets.
                              const untouched = ex.sets.every((d) => !d.w && !d.r && !d.s && !d.l && !d.raw);
                              if (g.defaultSets > 0 && untouched) {
                                patchExercise(sec.id, ex.id, {
                                  sets: Array.from({ length: Math.min(g.defaultSets, 6) }, () => ({ ...blankSet(), r: g.defaultReps || '' })),
                                });
                              }
                            }}
                            placeholder="Exercise name"
                            className="bg-transparent outline-none w-full text-[14px] font-semibold placeholder:text-fit-muted border-b border-transparent focus:border-fit-line-strong"
                          />
                          <button
                            onClick={() => patchSection(sec.id, { exercises: sec.exercises.filter((e) => e.id !== ex.id) })}
                            className="text-fit-muted hover:text-white text-[13px] px-1 shrink-0"
                            aria-label="Remove exercise"
                          >
                            ×
                          </button>
                        </div>

                        {lastWk && (
                          <p className="text-[11px] text-fit-muted mt-0.5">last wk · {lastWk}</p>
                        )}

                        {/* measure + unit — picks which set fields render below */}
                        <div className="flex items-center gap-1.5 mt-2">
                          <select
                            value={ex.measure}
                            onChange={(e) => patchExercise(sec.id, ex.id, { measure: e.target.value as MeasureType })}
                            className="bg-fit-bg-tertiary border border-fit-line rounded-md px-1.5 py-[3px] text-[10px] font-semibold uppercase tracking-[0.06em] text-fit-text-secondary outline-none focus:border-fit-line-strong cursor-pointer"
                            aria-label="Measure type"
                          >
                            {(Object.keys(MEASURE_LABELS) as MeasureType[]).map((m) => (
                              <option key={m} value={m}>{MEASURE_LABELS[m]}</option>
                            ))}
                          </select>
                          {(ex.measure === 'weight_reps' || ex.measure === 'laps') && (
                            <select
                              value={ex.unit}
                              onChange={(e) => patchExercise(sec.id, ex.id, { unit: e.target.value as WeightUnit })}
                              className="bg-fit-bg-tertiary border border-fit-line rounded-md px-1.5 py-[3px] text-[10px] font-semibold uppercase tracking-[0.06em] text-fit-text-secondary outline-none focus:border-fit-line-strong cursor-pointer"
                              aria-label="Weight unit"
                            >
                              <option value="lb">lb</option>
                              <option value="kg">kg</option>
                            </select>
                          )}
                        </div>

                        {/* sets — discrete fields matching the measure */}
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          {ex.sets.map((d, i) => (
                            <span key={i} className="relative inline-flex items-center gap-1 bg-fit-bg-tertiary border border-fit-line rounded-md px-2 py-1 focus-within:border-fit-line-strong">
                              {ex.measure === 'weight_reps' && (
                                <>
                                  <input value={d.w} inputMode="decimal" onChange={(e) => patchSet(sec.id, ex.id, i, { w: e.target.value })} placeholder="225" className="w-[44px] bg-transparent outline-none text-[12px] tabular-nums placeholder:text-fit-muted/40" />
                                  <span className="text-[10px] text-fit-muted">{ex.unit}</span>
                                  <span className="text-[10px] text-fit-muted">×</span>
                                  <input value={d.r} inputMode="numeric" onChange={(e) => patchSet(sec.id, ex.id, i, { r: e.target.value })} placeholder="10" className="w-[34px] bg-transparent outline-none text-[12px] tabular-nums placeholder:text-fit-muted/40" />
                                </>
                              )}
                              {ex.measure === 'time' && (
                                <>
                                  <input value={d.s} inputMode="numeric" onChange={(e) => patchSet(sec.id, ex.id, i, { s: e.target.value })} placeholder="60" className="w-[44px] bg-transparent outline-none text-[12px] tabular-nums placeholder:text-fit-muted/40" />
                                  <span className="text-[10px] text-fit-muted">sec</span>
                                </>
                              )}
                              {ex.measure === 'laps' && (
                                <>
                                  <input value={d.w} inputMode="decimal" onChange={(e) => patchSet(sec.id, ex.id, i, { w: e.target.value })} placeholder="160" className="w-[44px] bg-transparent outline-none text-[12px] tabular-nums placeholder:text-fit-muted/40" />
                                  <span className="text-[10px] text-fit-muted">{ex.unit} ×</span>
                                  <input value={d.l} inputMode="numeric" onChange={(e) => patchSet(sec.id, ex.id, i, { l: e.target.value })} placeholder="3" className="w-[28px] bg-transparent outline-none text-[12px] tabular-nums placeholder:text-fit-muted/40" />
                                  <span className="text-[10px] text-fit-muted">laps</span>
                                </>
                              )}
                              {ex.measure === 'bodyweight' && (
                                <>
                                  <input value={d.r} inputMode="numeric" onChange={(e) => patchSet(sec.id, ex.id, i, { r: e.target.value })} placeholder="12" className="w-[44px] bg-transparent outline-none text-[12px] tabular-nums placeholder:text-fit-muted/40" />
                                  <span className="text-[10px] text-fit-muted">reps</span>
                                </>
                              )}
                              {ex.measure === 'other' && (
                                <input value={d.raw} onChange={(e) => patchSet(sec.id, ex.id, i, { raw: e.target.value })} placeholder={i === 0 ? 'Blue band x 15' : `set ${i + 1}`} className="w-[110px] bg-transparent outline-none text-[12px] placeholder:text-fit-muted/40" />
                              )}
                              {ex.sets.length > 1 && (
                                <button
                                  onClick={() => patchExercise(sec.id, ex.id, { sets: ex.sets.filter((_, j) => j !== i) })}
                                  className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-fit-bg-elevated border border-fit-line text-fit-muted hover:text-white text-[9px] leading-none flex items-center justify-center"
                                  aria-label={`Remove set ${i + 1}`}
                                  tabIndex={-1}
                                >
                                  ×
                                </button>
                              )}
                            </span>
                          ))}
                          <button
                            onClick={() =>
                              patchExercise(sec.id, ex.id, {
                                // duplicate the previous set — matches the progression workflow
                                sets: [...ex.sets, { ...(ex.sets[ex.sets.length - 1] || blankSet()) }],
                              })
                            }
                            className="text-fit-text-secondary hover:text-white text-[13px] px-1.5 py-1 rounded-md hover:bg-white/5"
                            aria-label="Add set"
                          >
                            ＋ set
                          </button>
                        </div>

                        <input
                          value={ex.note}
                          onChange={(e) => patchExercise(sec.id, ex.id, { note: e.target.value })}
                          placeholder="note — box height, band color, tempo…"
                          className="bg-transparent outline-none w-full text-[11px] text-fit-text-secondary placeholder:text-fit-muted/50 mt-1.5 border-b border-transparent focus:border-fit-line"
                        />
                        <input
                          value={ex.videoUrl || ''}
                          onChange={(e) => patchExercise(sec.id, ex.id, { videoUrl: e.target.value })}
                          placeholder="demo video — https://youtube.com/watch?v=…"
                          className="bg-transparent outline-none w-full text-[11px] text-fit-text-secondary placeholder:text-fit-muted/50 mt-1 border-b border-transparent focus:border-fit-line"
                        />
                      </div>
                    );
                  })}

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setMenuFor(sec.id)}
                      className="btn btn-glass btn-sm"
                      title="Browse the exercise library — tap to add with last logged sets"
                    >
                      ⊞ Add from library
                    </button>
                    <button
                      onClick={() => patchSection(sec.id, { exercises: [...sec.exercises, blankExercise()] })}
                      className="text-[12px] text-fit-text-secondary hover:text-white px-1"
                    >
                      ＋ Type it in
                    </button>
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={() =>
                setSections((all) => [...all, { id: uid('sec'), type: 'custom', title: 'New Section', exercises: [blankExercise()] }])
              }
              className="self-start btn btn-glass btn-sm"
            >
              ＋ Section
            </button>
          </div>

          {/* ---------- footer ---------- */}
          <div className="flex items-center justify-between gap-3 pt-4 mt-4 border-t border-fit-line">
            {!isNew ? (
              confirmDelete ? (
                <div className="flex items-center gap-2">
                  <span className="text-[12px] text-fit-text-secondary">Delete this session?</span>
                  <button onClick={remove} className="text-[12px] font-semibold text-white underline underline-offset-2">Yes, delete</button>
                  <button onClick={() => setConfirmDelete(false)} className="text-[12px] text-fit-muted hover:text-white">Cancel</button>
                </div>
              ) : (
                <button onClick={() => setConfirmDelete(true)} className="text-[12px] text-fit-muted hover:text-white">Delete</button>
              )
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="btn btn-glass btn-sm">Cancel</button>
              <button onClick={save} className="btn btn-primary btn-sm">{isNew ? 'Add Session' : 'Save'}</button>
            </div>
          </div>
        </div>
      </div>

      {/* ---------- browsable exercise menu (real-time logging) ---------- */}
      {menuFor && (
        <ExerciseMenu
          clientId={session.clientId}
          sectionTitle={sections.find((s) => s.id === menuFor)?.title || 'section'}
          onAdd={addFromMenu}
          onClose={() => setMenuFor(null)}
        />
      )}
    </div>
  );
};

export default SessionEditor;
